import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, User, MailIcon, Building, HomeIcon, SearchIcon, BarChart as BarChartIcon, MousePointer, Calculator, LogOut, KeyRound, Save, Edit, X } from "lucide-react";
import { 
  Sidebar, 
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import AppSidebar from "@/components/AppSidebar";
import MobileNavbar from "@/components/MobileNavbar";
import SubscriptionManagement from "@/components/SubscriptionManagement";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Define o schema para validação do formulário
const userProfileSchema = z.object({
  nome: z.string().min(2, {
    message: "Nome deve ter pelo menos 2 caracteres",
  }),
  telefone: z.string().optional().transform(val => val || ""), // Transform empty values to empty string
  profissao: z.string().min(2, {
    message: "Profissão deve ser selecionada",
  }),
});

type UserProfileForm = z.infer<typeof userProfileSchema>;

// Opções de profissão
const PROFISSOES = [
  "Corretor",
  "Imobiliária",
  "Incorporador",
  "Investidor",
  "Outros"
];

const Admin = () => {
  const { user, profile, signOut, loading, resetPassword, updateProfile } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { state } = useSidebar();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Inicializar o formulário com os dados do perfil do usuário
  const form = useForm<UserProfileForm>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: {
      nome: "",
      telefone: "",
      profissao: "",
    },
  });

  // Atualizar o formulário quando o perfil for carregado
  useEffect(() => {
    if (profile) {
      form.reset({
        nome: profile.nome || "",
        telefone: profile.telefone || "",
        profissao: profile.profissao || "",
      });
    }
  }, [profile, form]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
      toast.success("Sessão encerrada com sucesso!");
    } catch (error) {
      console.error("Erro ao encerrar sessão:", error);
      toast.error("Erro ao encerrar sessão. Tente novamente.");
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    
    try {
      setIsResettingPassword(true);
      const { error } = await resetPassword(user.email);
      
      if (error) {
        toast.error("Erro ao solicitar redefinição de senha");
        console.error("Erro na redefinição de senha:", error);
      } else {
        toast.success("Email de redefinição de senha enviado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao redefinir senha:", error);
      toast.error("Erro ao solicitar redefinição de senha");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const onSubmit = async (data: UserProfileForm) => {
    try {
      setIsUpdating(true);
      
      // Ensure all fields are provided as required strings
      const profileData = {
        nome: data.nome,
        telefone: data.telefone || "", // Provide empty string if undefined
        profissao: data.profissao
      };
      
      const { error } = await updateProfile(profileData);
      
      if (error) {
        toast.error("Erro ao atualizar perfil");
        console.error("Erro ao atualizar perfil:", error);
      } else {
        toast.success("Perfil atualizado com sucesso!");
        setIsEditing(false); // Sair do modo de edição após salvar com sucesso
      }
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      toast.error("Erro ao atualizar perfil");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    // Restaurar os valores originais do formulário
    if (profile) {
      form.reset({
        nome: profile.nome || "",
        telefone: profile.telefone || "",
        profissao: profile.profissao || "",
      });
    }
    setIsEditing(false);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 text-blue-dark animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    navigate('/login');
    return null;
  }
  
  return (
    <div className="flex min-h-screen w-full bg-white">
      {/* Use AppSidebar component instead of inline sidebar */}
      <AppSidebar activePage="admin" />
      
      <div className={`flex-1 overflow-auto pt-6 ${isMobile ? 'pt-16' : ''} ${state === "collapsed" && !isMobile ? "ml-10" : ""}`}>
        <div className="container mx-auto max-w-4xl px-4">
          <h1 className="text-2xl font-bold mb-6">Perfil do Usuário</h1>
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-xl">Informações Pessoais</CardTitle>
              <CardDescription>Visualize e gerencie suas informações de conta</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-blue-light bg-opacity-30 flex items-center justify-center">
                    <User className="h-8 w-8 text-blue-dark" />
                  </div>
                  <div>
                    <h3 className="font-medium">{profile?.nome || (user.email ? user.email.split('@')[0] : 'Usuário')}</h3>
                    <p className="text-sm text-muted-foreground">Membro desde {new Date(user.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <h4 className="font-medium text-sm">E-mail</h4>
                  <div className="bg-muted/50 p-3 rounded-md flex items-center gap-2">
                    <MailIcon className="h-4 w-4 text-muted-foreground" />
                    <span>{user.email}</span>
                  </div>
                </div>
                
                <Separator className="my-2" />
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">Dados Pessoais</h3>
                      {!isEditing ? (
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setIsEditing(true)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Editar informações
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={handleCancelEdit}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Cancelar
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={isUpdating}
                          >
                            {isUpdating ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <Save className="mr-2 h-4 w-4" />
                                Salvar alterações
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="nome"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Seu nome" 
                              {...field} 
                              disabled={!isEditing}
                              className={!isEditing ? "bg-muted/50" : ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="telefone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Telefone</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Seu telefone" 
                              {...field} 
                              disabled={!isEditing}
                              className={!isEditing ? "bg-muted/50" : ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="profissao"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Profissão</FormLabel>
                          <Select
                            disabled={!isEditing}
                            onValueChange={field.onChange}
                            value={field.value}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className={!isEditing ? "bg-muted/50" : ""}>
                                <SelectValue placeholder="Selecione sua profissão" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {PROFISSOES.map((profissao) => (
                                <SelectItem key={profissao} value={profissao}>
                                  {profissao}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </div>
            </CardContent>
          </Card>

          <div className="mb-8">
            <SubscriptionManagement />
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Segurança da Conta</CardTitle>
              <CardDescription>Gerenciar a segurança da sua conta</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1">Redefinir Senha</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Enviaremos um email com instruções para redefinir sua senha
                  </p>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2"
                    onClick={handleResetPassword}
                    disabled={isResettingPassword}
                  >
                    {isResettingPassword ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <KeyRound className="h-4 w-4" />
                        Redefinir senha
                      </>
                    )}
                  </Button>
                </div>
                
                <Separator className="my-2" />
                
                <div>
                  <h3 className="font-medium mb-1">Sair da Conta</h3>
                  <p className="text-sm text-muted-foreground mb-2">Encerrar sua sessão atual no sistema</p>
                  <Button 
                    variant="outline" 
                    className="border-destructive text-destructive hover:bg-destructive/10"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Admin;
